export default function EntryStrategySection() {
  return (
    <section id="entry-strategy" className="relative py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="max-w-3xl mb-12">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-slate-100 rounded-md border border-slate-200 mb-6">
            <i className="ri-store-line text-slate-600 text-sm"></i>
            <span className="text-slate-600 text-sm font-medium tracking-wide">進場策略分析</span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-black text-slate-900 leading-tight mb-6">
            兩種進場模型
          </h2>

          <p className="text-lg text-slate-600 leading-relaxed">
            金門的餐飲機會可切成兩條清晰路徑：中小型連鎖品牌先吃「第一排人流」；大型品牌用「獨棟旗艦」承接專車與團客。兩條路徑可同時成立。
          </p>
        </div>

        {/* Two Paths */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* Path A */}
          <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-lg border-2 border-orange-200 overflow-hidden">
            <div className="p-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-orange-600 rounded-md">
                  <span className="text-white text-xl font-black">A</span>
                </div>
                <div>
                  <h3 className="text-2xl font-black text-slate-900">中小型連鎖品牌</h3>
                  <p className="text-slate-600 text-sm">第一排店面＝最高轉換率</p>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div className="bg-white rounded-lg p-4 border border-orange-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="w-6 h-6 flex items-center justify-center bg-orange-600 rounded text-white text-xs font-bold">1</span>
                    <span className="text-slate-900 font-bold text-sm">人流可視化</span>
                  </div>
                  <p className="text-slate-600 text-xs">客流與車流穩定，消費決策時間短</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-orange-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="w-6 h-6 flex items-center justify-center bg-orange-600 rounded text-white text-xs font-bold">2</span>
                    <span className="text-slate-900 font-bold text-sm">高頻需求</span>
                  </div>
                  <p className="text-slate-600 text-xs">外帶、快速用餐、晚餐宵夜場景完整</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-orange-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="w-6 h-6 flex items-center justify-center bg-orange-600 rounded text-white text-xs font-bold">3</span>
                    <span className="text-slate-900 font-bold text-sm">高坪效模型</span>
                  </div>
                  <p className="text-slate-600 text-xs">小面積可達高翻桌／高出餐頻率</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                <span className="px-3 py-1 bg-orange-200 text-orange-800 text-xs font-medium rounded-full">鹹酥雞</span>
                <span className="px-3 py-1 bg-orange-200 text-orange-800 text-xs font-medium rounded-full">鐵板燒</span>
                <span className="px-3 py-1 bg-orange-200 text-orange-800 text-xs font-medium rounded-full">外帶型品牌</span>
              </div>

              <div className="bg-orange-100 rounded-lg p-4 border border-orange-200">
                <div className="text-orange-700 font-bold text-xs mb-1">選址硬條件</div>
                <p className="text-slate-600 text-xs">主要道路可視性高、一樓臨路、外帶動線順、停車友善</p>
              </div>
            </div>
          </div>

          {/* Path B */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-lg border-2 border-blue-200 overflow-hidden">
            <div className="p-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-md">
                  <span className="text-white text-xl font-black">B</span>
                </div>
                <div>
                  <h3 className="text-2xl font-black text-slate-900">大型旗艦品牌</h3>
                  <p className="text-slate-600 text-sm">獨棟旗艦＝承接團客人流量</p>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="w-6 h-6 flex items-center justify-center bg-blue-600 rounded text-white text-xs font-bold">1</span>
                    <span className="text-slate-900 font-bold text-sm">人流量</span>
                  </div>
                  <p className="text-slate-600 text-xs">同時容納多組團客與家庭聚餐</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="w-6 h-6 flex items-center justify-center bg-blue-600 rounded text-white text-xs font-bold">2</span>
                    <span className="text-slate-900 font-bold text-sm">動線設計</span>
                  </div>
                  <p className="text-slate-600 text-xs">專車上下客、候位區、出餐節奏可標準化</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="w-6 h-6 flex items-center justify-center bg-blue-600 rounded text-white text-xs font-bold">3</span>
                    <span className="text-slate-900 font-bold text-sm">目的地效應</span>
                  </div>
                  <p className="text-slate-600 text-xs">品牌本身成為行程亮點，形成「指定用餐」</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                <span className="px-3 py-1 bg-blue-200 text-blue-800 text-xs font-medium rounded-full">大型中式連鎖</span>
                <span className="px-3 py-1 bg-blue-200 text-blue-800 text-xs font-medium rounded-full">指標性品牌</span>
                <span className="px-3 py-1 bg-blue-200 text-blue-800 text-xs font-medium rounded-full">目的地餐廳</span>
              </div>

              <div className="bg-blue-100 rounded-lg p-4 border border-blue-200">
                <div className="text-blue-700 font-bold text-xs mb-1">選址硬條件</div>
                <p className="text-slate-600 text-xs">遊覽車上下客動線、停車場規模足夠、具備地標性</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
